import { useEffect, useState } from "react";

const SEEN_KEY = "willoah.splash.seen.v1";

export default function SplashScreen() {
  // Skip immediately if seen this session; also skip inside OAuth callback fragments
  // and on public embed routes so iframes render cleanly.
  const [phase, setPhase] = useState(() => {
    if (typeof window === "undefined") return "gone";
    if (window.location.hash?.includes("session_id=")) return "gone";
    if (window.location.pathname?.startsWith("/embed/")) return "gone";
    try {
      if (sessionStorage.getItem(SEEN_KEY)) return "gone";
    } catch {}
    return "in"; // "in" -> "out" -> "gone"
  });

  useEffect(() => {
    if (phase !== "in") return;
    const outTimer = setTimeout(() => setPhase("out"), 1600); // hold
    const goneTimer = setTimeout(() => {
      try { sessionStorage.setItem(SEEN_KEY, "1"); } catch {}
      setPhase("gone");
    }, 2200); // total 2.2s
    return () => {
      clearTimeout(outTimer);
      clearTimeout(goneTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(SEEN_KEY, "1"); } catch {}
    setPhase("gone");
  };

  if (phase === "gone") return null;

  return (
    <div
      data-testid="willoah-splash"
      role="presentation"
      onClick={dismiss}
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-[#FBF8F1] transition-opacity duration-500 ease-out ${
        phase === "out" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{
        // Subtle grain via radial gradient so the cream doesn't look flat
        backgroundImage:
          "radial-gradient(circle at 30% 20%, rgba(183,146,86,0.06), transparent 55%), radial-gradient(circle at 75% 80%, rgba(78,90,61,0.05), transparent 60%)",
      }}
    >
      <div
        className="flex flex-col items-center gap-4 px-8"
        style={{
          animation: "willoahReveal 900ms ease-out both",
        }}
      >
        <img
          src="/willow-wordmark.png"
          alt="Willoah — real life. real ideas."
          className="w-[min(560px,80vw)] h-auto select-none"
          draggable={false}
        />
        <span
          data-testid="willoah-splash-tagline"
          className="sr-only"
        >
          Willoah — real life. real ideas.
        </span>
      </div>

      <style>{`
        @keyframes willoahReveal {
          0%   { opacity: 0; transform: translateY(6px) scale(0.985); filter: blur(2px); }
          60%  { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="willoah-splash"] > div { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
